import { X } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef } from 'react';

/**
 * Ventana modal sin Radix: un `<dialog>` nativo abierto con `showModal()` (foco atrapado y el resto de la
 * página inerte, como en la Power App). El fondo atenuado es el `::backdrop`: fijo, cubre la pantalla y usa
 * `bg-black/55`, el de `src/index.css`.
 *
 * Solo existe mientras está abierta: quien la usa la monta cuando `open` y la quita al cerrar, así que el
 * formulario de dentro no está en la página con la ventana cerrada. Se cierra con la X, con Escape o con el
 * botón de cancelar del formulario (todos llaman a `onClose`, sin enviar nada). El nombre accesible es el
 * título y, si la hay, la descripción la describe.
 */
export function Modal({
  title,
  description,
  onClose,
  children,
}: {
  title: ReactNode;
  description?: string;
  onClose: () => void;
  /** El formulario, con su pie (`ModalFooter`) dentro para que sus botones lo envíen. */
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => {
      if (dialog?.open) dialog.close();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      // Escape: el navegador lanza `cancel`; se evita su cierre propio y decide `onClose` (el estado de React).
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onClose();
        }
      }}
      className="m-auto max-h-[90vh] w-[calc(100%-2rem)] max-w-xl overflow-y-auto rounded-xl border border-border bg-card p-0 text-card-foreground shadow-xl backdrop:fixed backdrop:inset-0 backdrop:bg-black/55"
    >
      <div className="flex items-start justify-between gap-4 border-b border-border p-5">
        <div className="min-w-0 space-y-1.5">
          <h2 id={titleId} className="text-lg leading-none font-semibold tracking-tight">
            {title}
          </h2>
          {description ? (
            <p id={descriptionId} className="text-sm text-muted-foreground">
              {description}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          aria-label="Cerrar"
          onClick={onClose}
          className="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none"
        >
          <X aria-hidden className="size-4" />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </dialog>
  );
}

/** Pie de la ventana, dentro del formulario: los botones a la derecha, separados por un borde. */
export function ModalFooter({ children }: { children: ReactNode }) {
  return <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">{children}</div>;
}
